import { useEffect, useState } from "react";

/**
 * Page Visibility Hook
 *
 * Tracks whether the current page/tab is visible to the user.
 * Returns true when page is visible, false when hidden/backgrounded.
 *
 * This hook enables pausing expensive operations (like Firestore real-time
 * listeners) when the tab is in the background, reducing costs and improving
 * performance.
 *
 * Browser Support: All modern browsers (IE 10+)
 * @see https://developer.mozilla.org/en-US/docs/Web/API/Page_Visibility_API
 */
export function usePageVisibility(): boolean {
  // Always start as visible. PWA cold-starts on iOS briefly report
  // visibilityState === "hidden" before becoming visible, which would
  // otherwise prevent the initial Firestore subscription from running
  // and leave consumers with stale/empty cached data.
  // Real backgrounding still pauses listeners via the visibilitychange event.
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    if (typeof document === "undefined") return;

    const handleVisibilityChange = () => {
      setIsVisible(document.visibilityState === "visible");
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  return isVisible;
}
